// Define API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Interface for password reset response
interface ResetPasswordResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Send password reset request to the server
 * @param email - User's email to send reset link
 * @returns Promise with reset response
 */
export const requestPasswordReset = async (email: string): Promise<ResetPasswordResponse> => {
  try {
    // Create a cancellation token with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email }),
      signal: controller.signal
    });
    
    // Clear timeout
    clearTimeout(timeoutId);
    
    if (response.ok) {
      return {
        success: true,
        message: 'Password reset email sent successfully'
      };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message || 'Failed to reset password',
        error: errorData.error || 'Unknown error'
      };
    }
  } catch (error: any) {
    // Handle network errors
    if (error.name === 'AbortError') {
      console.warn('Request timed out');
      return {
        success: false,
        message: 'Request timed out. Server may be unavailable.',
        error: 'TIMEOUT'
      };
    }
    
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.warn('Network error - server likely offline');
      return {
        success: false,
        message: 'Unable to connect to server.',
        error: 'NETWORK_ERROR'
      };
    }
    
    return {
      success: false,
      message: error.message || 'An unexpected error occurred',
      error: 'UNKNOWN'
    };
  }
};

/**
 * Check if the server is available
 * @returns Promise<boolean> - true if server is available
 */
export const checkServerStatus = async (): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch(`${API_URL}/auth/status`, {
      method: 'HEAD',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.warn('Server status check failed:', error);
    return false;
  }
};

/**
 * Reset password with token
 * @param token - Reset password token
 * @param newPassword - New password
 * @returns Promise with reset response
 */
export const resetPassword = async (token: string, newPassword: string): Promise<ResetPasswordResponse> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${API_URL}/auth/reset-password/${token}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password: newPassword }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      return {
        success: true,
        message: 'Password reset successful'
      };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message || 'Failed to reset password',
        error: errorData.error || 'Unknown error'
      };
    }
  } catch (error: any) {
    // Handle network errors
    if (error.name === 'AbortError') {
      return {
        success: false,
        message: 'Request timed out. Server may be unavailable.',
        error: 'TIMEOUT'
      };
    }
    
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return {
        success: false,
        message: 'Unable to connect to server.',
        error: 'NETWORK_ERROR'
      };
    }
    
    return {
      success: false,
      message: error.message || 'An unexpected error occurred',
      error: 'UNKNOWN'
    };
  }
};

/**
 * Login user
 * @param username - Username
 * @param password - Password
 * @returns Promise with login response containing token and user info
 */
export const login = async (username: string, password: string) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        token: data.token,
        user: data.user
      };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message || 'Invalid credentials'
      };
    }
  } catch (error: any) {
    // Handle specific errors
    if (error.name === 'AbortError' || 
        (error instanceof TypeError && error.message === 'Failed to fetch')) {
      return {
        success: false,
        message: 'Unable to connect to server.'
      };
    }
    
    return {
      success: false,
      message: error.message || 'Login failed'
    };
  }
}; 